// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

import type { PartialDeep } from 'type-fest';

import type { ExcludeMethodsDeep } from '../../../utils/types';
import { NIL_UUID } from '../../../utils/uuid';
import Enums from '../Enums';

export class Tenant {
  readonly id: string;
  readonly name: string;
  readonly useProjectNumberAutogeneration: boolean;
  readonly defaultDryVolumeUnit: Enums.Unit;
  readonly defaultWetVolumeUnit: Enums.Unit;
  readonly defaultLengthUnit: Enums.Unit;
  readonly defaultAreaUnit: Enums.Unit;
  readonly defaultWeightUnit: Enums.Unit;
  readonly defaultOtherUnit: Enums.Unit;
  readonly defaultStrengthUnit: Enums.Unit;
  readonly defaultCurrencyCode: Enums.CurrencyCode;
  readonly targetMarginPercentage: string;
  readonly minimumMarginPercentage: string;
  readonly targetMarginFlatAmount: string;
  readonly minimumMarginFlatAmount: string;
  readonly marginConfiguration: Enums.MarginConfiguration;
  readonly locale: string;
  readonly timezone: string | null;
  readonly quoteNumberPrefix: string;
  readonly quoteNumberDigits: number | null;
  readonly locationAutoSelect: boolean;
  readonly tierPricing: boolean;
  readonly approvalLadderMode: Enums.QuoteApprovalLadderMode;
  readonly lowerTierPriceDeviationThreshold: string | null;
  readonly upperTierPriceDeviationThreshold: string | null;
  readonly hasImportedPricing: boolean;
  readonly defaultQuoteValidityDays: number;
  readonly defaultProjectValidityDays: number | null;
  readonly defaultProjectBidDateDays: number | null;
  readonly inheritQuoteExpirationFromProject: boolean;
  readonly restrictActivityEditingToAssignees: boolean;
  readonly defaultCc: string[];
  readonly defaultBcc: string[];

  constructor(data: { [key: string]: any } = {}) {
    this.id = data.id === undefined ? NIL_UUID : data.id;
    this.name = data.name === undefined ? '' : data.name;
    this.useProjectNumberAutogeneration =
      data.useProjectNumberAutogeneration === undefined
        ? false
        : data.useProjectNumberAutogeneration;
    this.defaultDryVolumeUnit =
      data.defaultDryVolumeUnit === undefined ? Enums.Unit.CuYd : data.defaultDryVolumeUnit;
    this.defaultWetVolumeUnit =
      data.defaultWetVolumeUnit === undefined ? Enums.Unit.Gal : data.defaultWetVolumeUnit;
    this.defaultLengthUnit =
      data.defaultLengthUnit === undefined ? Enums.Unit.Mi : data.defaultLengthUnit;
    this.defaultAreaUnit =
      data.defaultAreaUnit === undefined ? Enums.Unit.SqYd : data.defaultAreaUnit;
    this.defaultWeightUnit =
      data.defaultWeightUnit === undefined ? Enums.Unit.Tn : data.defaultWeightUnit;
    this.defaultOtherUnit =
      data.defaultOtherUnit === undefined ? Enums.Unit.Ea : data.defaultOtherUnit;
    this.defaultStrengthUnit =
      data.defaultStrengthUnit === undefined ? Enums.Unit.Psi : data.defaultStrengthUnit;
    this.defaultCurrencyCode =
      data.defaultCurrencyCode === undefined ? Enums.CurrencyCode.USD : data.defaultCurrencyCode;
    this.targetMarginPercentage =
      data.targetMarginPercentage === undefined ? '0' : data.targetMarginPercentage;
    this.minimumMarginPercentage =
      data.minimumMarginPercentage === undefined ? '0' : data.minimumMarginPercentage;
    this.targetMarginFlatAmount =
      data.targetMarginFlatAmount === undefined ? '0' : data.targetMarginFlatAmount;
    this.minimumMarginFlatAmount =
      data.minimumMarginFlatAmount === undefined ? '0' : data.minimumMarginFlatAmount;
    this.marginConfiguration =
      data.marginConfiguration === undefined
        ? Enums.MarginConfiguration.Percentage
        : data.marginConfiguration;
    this.locale = data.locale === undefined ? 'en-US' : data.locale;
    this.timezone = data.timezone === undefined ? null : data.timezone;
    this.quoteNumberPrefix =
      data.quoteNumberPrefix === undefined ? 'YYYY-' : data.quoteNumberPrefix;
    this.quoteNumberDigits = data.quoteNumberDigits === undefined ? null : data.quoteNumberDigits;
    this.locationAutoSelect =
      data.locationAutoSelect === undefined ? false : data.locationAutoSelect;
    this.tierPricing = data.tierPricing === undefined ? false : data.tierPricing;
    this.approvalLadderMode =
      data.approvalLadderMode === undefined
        ? Enums.QuoteApprovalLadderMode.Inclusive
        : data.approvalLadderMode;
    this.lowerTierPriceDeviationThreshold =
      data.lowerTierPriceDeviationThreshold === undefined
        ? null
        : data.lowerTierPriceDeviationThreshold;
    this.upperTierPriceDeviationThreshold =
      data.upperTierPriceDeviationThreshold === undefined
        ? null
        : data.upperTierPriceDeviationThreshold;
    this.hasImportedPricing =
      data.hasImportedPricing === undefined ? false : data.hasImportedPricing;
    this.defaultQuoteValidityDays =
      data.defaultQuoteValidityDays === undefined ? 30 : data.defaultQuoteValidityDays;
    this.defaultProjectValidityDays =
      data.defaultProjectValidityDays === undefined ? null : data.defaultProjectValidityDays;
    this.defaultProjectBidDateDays =
      data.defaultProjectBidDateDays === undefined ? null : data.defaultProjectBidDateDays;
    this.inheritQuoteExpirationFromProject =
      data.inheritQuoteExpirationFromProject === undefined
        ? false
        : data.inheritQuoteExpirationFromProject;
    this.restrictActivityEditingToAssignees =
      data.restrictActivityEditingToAssignees === undefined
        ? false
        : data.restrictActivityEditingToAssignees;
    this.defaultCc = data.defaultCc ?? [];
    this.defaultBcc = data.defaultBcc ?? [];
  }

  static zero(): Tenant {
    const zeroValues: ExcludeMethodsDeep<Tenant> = {
      id: NIL_UUID,
      name: '',
      useProjectNumberAutogeneration: false,
      defaultDryVolumeUnit: Enums.Unit.CuYd,
      defaultWetVolumeUnit: Enums.Unit.Gal,
      defaultLengthUnit: Enums.Unit.Mi,
      defaultAreaUnit: Enums.Unit.SqYd,
      defaultWeightUnit: Enums.Unit.Tn,
      defaultOtherUnit: Enums.Unit.Ea,
      defaultStrengthUnit: Enums.Unit.Psi,
      defaultCurrencyCode: Enums.CurrencyCode.USD,
      targetMarginPercentage: '0',
      minimumMarginPercentage: '0',
      targetMarginFlatAmount: '0',
      minimumMarginFlatAmount: '0',
      marginConfiguration: Enums.MarginConfiguration.Percentage,
      locale: 'en-US',
      timezone: null,
      quoteNumberPrefix: 'YYYY-',
      quoteNumberDigits: null,
      locationAutoSelect: false,
      tierPricing: false,
      approvalLadderMode: Enums.QuoteApprovalLadderMode.Inclusive,
      lowerTierPriceDeviationThreshold: null,
      upperTierPriceDeviationThreshold: null,
      hasImportedPricing: false,
      defaultQuoteValidityDays: 30,
      defaultProjectValidityDays: null,
      defaultProjectBidDateDays: null,
      inheritQuoteExpirationFromProject: false,
      restrictActivityEditingToAssignees: false,
      defaultCc: [],
      defaultBcc: [],
    };
    return new Tenant(zeroValues);
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************
  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type TenantWireFormat = {
  id: string;
  name: string;
  useProjectNumberAutogeneration: boolean;
  defaultDryVolumeUnit: string;
  defaultWetVolumeUnit: string;
  defaultLengthUnit: string;
  defaultAreaUnit: string;
  defaultWeightUnit: string;
  defaultOtherUnit: string;
  defaultStrengthUnit: string;
  defaultCurrencyCode: string;
  targetMarginPercentage: string;
  minimumMarginPercentage: string;
  targetMarginFlatAmount: string;
  minimumMarginFlatAmount: string;
  marginConfiguration: string;
  locale: string;
  timezone: string | null;
  quoteNumberPrefix: string;
  quoteNumberDigits: number | null;
  locationAutoSelect: boolean;
  tierPricing: boolean;
  approvalLadderMode: string;
  lowerTierPriceDeviationThreshold: string | null;
  upperTierPriceDeviationThreshold: string | null;
  hasImportedPricing: boolean;
  defaultQuoteValidityDays: number;
  defaultProjectValidityDays: number | null;
  defaultProjectBidDateDays: number | null;
  inheritQuoteExpirationFromProject: boolean;
  restrictActivityEditingToAssignees: boolean;
  defaultCc: string[];
  defaultBcc: string[];
};

type NewTenantProps = PartialDeep<Tenant, { recurseIntoArrays: true }>;
export const NewTenant: (props: NewTenantProps) => Tenant = (props) => new Tenant(props);

type NewTenantFromWireFormatProps = PartialDeep<TenantWireFormat, { recurseIntoArrays: true }>;
export const NewTenantFromWireFormat: (props: NewTenantFromWireFormatProps) => Tenant = (props) =>
  new Tenant(props);
